import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  Grid,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import AccountServices from 'services/AccountServices';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import BillDetailsDialog from 'components/popups/BillDetailsDialog';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import CustomerService from 'services/CustomerService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { STORAGE_KEYS, SelectFilterStyle, SALE_SOURCES, SALE_STATUS, PRINT_TYPES, USER_PERMISSIONS } from 'const';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import { TerminalContext } from 'Context/TerminalContext';
import { UserContext } from 'Context/UserContext';
import CsvHelper from 'helpers/CsvHelper';
import TallyXmlHelper from 'helpers/TallyXmlHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import { getFilteredCSVColumns } from 'components/utils/FilteredColumns';
import SaleInvoice from 'components/utils/InvoiceDownload/SaleInvoice';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import RefundTypePopup from 'components/popups/RefundTypePopup';
import { useTranslation } from 'react-i18next';
import { useReportPrint } from 'hooks/useReportPrint';

function BillSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { terminals: terminalsFromContext } = useContext(TerminalContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { user } = useContext(UserContext);

  const showWriteActions = user?.shop_permissions?.reports_permission !== USER_PERMISSIONS.READ;

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [sorting, setSorting] = useState('default');
  const [filterStatus, setFilterStatus] = useState(SALE_STATUS.active);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    sorting,
    filterStatus,
  });
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [customers, setCustomers] = useState('');
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const [invoiceToVoid, setInvoiceToVoid] = useState(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  const [filterCustomer, setFilterCustomer] = useState([]);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [filterCustomerGroup, setFilterCustomerGroup] = useState([]);
  const [filterSource, setFilterSource] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);
  const terminalMap = useMemo(
    () => Object.fromEntries(terminalsFromContext.map((terminal) => [terminal.id, terminal.name])),
    [terminalsFromContext]
  );
  const customerMap = useMemo(
    () =>
      Object.fromEntries((Array.isArray(customers) ? customers : []).map((customer) => [customer.id, customer.name])),
    [customers]
  );
  const salesPersonMap = useMemo(
    () => Object.fromEntries(salesPersons.map((person) => [person.id, person.name])),
    [salesPersons]
  );
  const customerGroupMap = useMemo(
    () => Object.fromEntries(customerGroups.map((group) => [group.id, group.name])),
    [customerGroups]
  );
  const saleSourceMap = useMemo(() => Object.fromEntries(SALE_SOURCES.map((source) => [source.id, source.label])), []);

  const CSV_COLUMNS = {
    bill_no: {
      title: 'Bill No',
      getValue: (datum) => datum.bill_no || datum.fid,
    },
    date: 'Date',
    time: {
      title: 'Time',
      getValue: (datum) => DateTimeHelpers.formatServerTimeSegment(datum.time),
    },
    customer_name: 'Customer',
    customer_mobile: 'Mobile',
    customer_tax_id: 'Tax ID',
    discount: {
      title: 'Discount',
      getValue: (datum) => datum.discount + datum.discount_applied_on_items,
    },
    net_amount: {
      title: 'Net Amount',
      getValue: (datum) =>
        ShopsHelper.getAmountFormatted(
          shop,
          datum.total - datum.tax_amount + (datum.discount + datum.discount_applied_on_items)
        ),
    },
    tax_amount: {
      title: 'Tax Amount',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax_amount || 0);
      },
    },
    cess_amount: {
      title: 'Additional Tax',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.cess_amount || 0);
      },
    },
    total: {
      title: 'Total Amount',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      sorting !== prevFilterData.sorting ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson ||
      filterCustomerGroup !== prevFilterData.filterCustomerGroup ||
      filterSource !== prevFilterData.filterSource ||
      filterStatus !== prevFilterData.filterStatus ||
      filterCustomer !== prevFilterData.filterCustomer
    );
  }, [
    filterFromDate,
    filterToDate,
    sorting,
    filterTerminal,
    filterSalesperson,
    filterCustomerGroup,
    filterSource,
    filterStatus,
    filterCustomer,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.sorting,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
    prevFilterData.filterCustomerGroup,
    prevFilterData.filterSource,
    prevFilterData.filterStatus,
    prevFilterData.filterCustomer,
  ]);

  useEffect(() => {
    // getMachineName();
    if (ShopsHelper.hasPremiumAccess(shop)) {
      getCustomers();
      getSalespersons();
      getCustomerGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    generateSummeryReport();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(tReport('Sales Summary Report'), filterFromDate, filterToDate, headerData, filteredItems, {
      [tCommon('Net Amount')]: summary.totalNetAmount,
      [tCommon('Tax Amount')]: summary.totalTax,
      [tCommon('Additional Tax')]: summary.totalAdditionalTax,
      [tCommon('Discount')]: summary.discount,
      [tCommon('Total Amount')]: summary.totalAmount,
    });
  };

  const getCustomerGroups = async () => {
    try {
      const res = await CustomerService.getCustomerGroups();
      setCustomerGroups(res);
    } catch (err) {
      console.error('Failed to fetch customer groups', err);
    }
  };

  const generateSummeryReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('BILL_SUMMARY');
    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);
    loadBillSummaryReports();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
      sorting,
      filterSalesperson,
      filterTerminal,
      filterCustomerGroup,
      filterSource,
      filterStatus,
      filterCustomer,
    });
  };

  const loadBillSummaryReports = async ({
    startTime,
    endTime,
    sort = sorting,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
    customer_group_ids = filterCustomerGroup,
    sources = filterSource,
    status = filterStatus,
    customer_ids = filterCustomer,
  } = {}) => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('BILL_SUMMARY');

    const start = startTime || dynamicStartDate;
    const end = endTime || dynamicEndDate;

    if (DateTimeHelpers.reportDateLimit(start, end)) {
      setDateLimitPopup(true);
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
        sort_bill_num: sort === 'default' ? undefined : sort,
        customer_group_ids: customer_group_ids.length ? customer_group_ids : null,
        sources: sources.length ? sources : null,
        status,
        customer_ids: customer_ids.length ? customer_ids : null,
      };

      const resp = await BillSummaryReportService.getBillSummaryReport(data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }

    setLoadingIndicator(false);
  };

  const getCustomers = async () => {
    const res = await CustomerService.getCustomers();
    setCustomers(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const toCsvBtnPressed = async () => {
    const filteredCsvColumns = getFilteredCSVColumns(
      CSV_COLUMNS,
      optionalColumns,
      STORAGE_KEYS.SALES_REPORT_COLUMN_FILTER
    );

    const titleLine = tReport('Sales Summary Report for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, filteredCsvColumns, titleLine);

    // Create total row
    const summary = _getBillReportSummary(filteredItems);
    const fields = Object.keys(filteredCsvColumns);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'date':
          return tCommon('Total');
        case 'discount':
          return summary.discount;
        case 'net_amount':
          return summary.totalNetAmount;
        case 'tax_amount':
          return summary.totalTax;
        case 'cess_amount':
          return summary.totalAdditionalTax;
        case 'total':
          return summary.totalAmount;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `SALES-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const toTallyXmlBtnPressed = () => {
    if (!filteredItems || filteredItems.length === 0) {
      postAlertMessage({ text: tReport('No data available to export'), type: 'failed' });
      return;
    }

    try {
      const xmlContent = TallyXmlHelper.generateTallySalesXml(filteredItems, {
        companyName: shop?.name || shop?.company_name || 'MACHAD MILL',
        state: shop?.state || 'Kerala',
        gstin: shop?.tax_number || shop?.gstin || shop?.config?.gstin || '',
        userName: user?.name || shop?.name || 'User',
      });

      const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
      const dateTime = DateTimeHelpers.getDate(new Date());
      saveAs(blob, `TALLY-SALES-${dateTime}.xml`);

      postAlertMessage({
        text: tReport('Exported to Tally XML successfully'),
        type: 'success',
      });
    } catch (err) {
      postAlertMessage({
        text: err?.message || tReport('Failed to export Tally XML'),
        type: 'failed',
      });
    }
  };

  const handleCloseSaleDetailsDialog = () => {
    setSelectedBill(null);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(
      items.filter(
        (item) =>
          item.bill_no.toLowerCase().includes(value.toLowerCase()) ||
          item.date.toLowerCase().includes(value.toLowerCase()) ||
          item.time.toLowerCase().includes(value.toLowerCase()) ||
          (item.customer_name && item.customer_name.toLowerCase().includes(value.toLowerCase()))
      )
    );
  };

  const handleClickVoidInvoiceBtn = (invoice) => {
    setInvoiceToVoid(invoice);
  };

  const handleVoidInvoice = async (refundType) => {
    try {
      await BillSummaryReportService.voidInvoice(invoiceToVoid?.id, { refund_type: refundType });
      setInvoiceToVoid(null);

      try {
        await AccountServices.syncAccountLedger();
      } catch (e) {
        console.error('Ledger sync failed', e);
      }

      postAlertMessage({ text: tReport('Void invoice successfully'), type: 'success' });
      loadBillSummaryReports();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  };

  const _getBillReportSummary = (items) => {
    let totalNetAmount = 0;
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let discount = 0;
    items.forEach((item) => {
      totalNetAmount += item.total - item.tax_amount + (item.discount + item.discount_applied_on_items);
      totalAmount += item.total;
      totalTax += item.tax_amount;
      totalAdditionalTax += item.cess_amount;
      discount += item.discount_applied_on_items + item.discount;
    });

    return {
      totalNetAmount: ShopsHelper.getAmountFormatted(shop, totalNetAmount),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      discount: ShopsHelper.getAmountFormatted(shop, discount),
    };
  };

  const headerData = [
    {
      label: tCommon('Bill No'),
      id: 'bill_no',
      type: 'callback',
      viewRender: (obj) => (obj.bill_no ? obj.bill_no : obj.fid),
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.formatServerTimeSegment(item.time),
    },
    {
      label: tCommon('Customer Name'),
      id: 'customer_name',
      type: 'text',
    },
    {
      label: tCommon('Mobile'),
      id: 'customer_mobile',
      type: 'text',
    },
    {
      label: tCommon('Tax ID'),
      id: 'customer_tax_id',
      type: 'text',
    },
    {
      label: tCommon('Discount'),
      id: 'discount_applied_on_items',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(
          shop,
          item.discount + item.discount_applied_on_items,
          shop?.tax_region
        )}`;
      },
    },
    {
      label: tCommon('Net Amount'),
      id: 'net_amount',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(
          shop,
          item.total - item.tax_amount - item.cess_amount + (item.discount + item.discount_applied_on_items),
          shop?.tax_region
        )}`;
      },
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
      },
    },
    {
      label: tCommon('Actions'),
      id: 'download',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (bill) => (
        <Grid className={styles.btnGroup}>
          <Tooltip title={tCommon('View Items')} onClick={() => setSelectedBill(bill)}>
            <RemoveRedEyeIcon className={styles.iconBtn} />
          </Tooltip>
          {showWriteActions && (
            <Tooltip title={tCommon('Void Invoice')} onClick={() => handleClickVoidInvoiceBtn(bill)}>
              <RestartAltIcon className={styles.iconBtn} />
            </Tooltip>
          )}
          <SaleInvoice bill={bill} showOptions={showWriteActions} />
        </Grid>
      ),
    },
  ];

  const optionalColumns = {
    customer_name: true,
    customer_mobile: false,
    customer_tax_id: false,
    discount: true,
    net_amount: true,
    tax_amount: true,
    cess_amount: true,
    view_Items: true,
    download: true,
  };

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tCommon('CSV')}
            </Button>
            <IconButton
              onClick={handleExportMenuOpen}
              style={{
                backgroundColor: '#0284c7',
                color: '#fff',
                borderRadius: '4px',
                width: '40px',
                height: '40px',
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={exportMenuAnchorEl}
              open={Boolean(exportMenuAnchorEl)}
              onClose={handleExportMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  handlePrintReport();
                }}
              >
                <PrintIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('Print')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  toCsvBtnPressed();
                }}
              >
                <ImportExportIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('CSV')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  toTallyXmlBtnPressed();
                }}
              >
                <ImportExportIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('Tally XML')}
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Sales Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tReport('Search by bill no, date')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="BILL_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminalMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminalsFromContext.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          {ShopsHelper.hasPremiumAccess(shop) && (
            <>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Customer')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterCustomer}
                      onChange={(e) => setFilterCustomer(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterCustomer) => {
                        if (filterCustomer.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterCustomer.map((id) => customerMap[id]).filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      <ListSubheader>
                        <TextField
                          size="small"
                          autoFocus
                          placeholder={tCommon('Search')}
                          fullWidth
                          InputProps={{
                            startAdornment: <SearchIcon style={{ color: 'grey' }} />,
                          }}
                          onChange={(e) => setCustomerSearchText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                              e.stopPropagation();
                            }
                          }}
                        />
                      </ListSubheader>
                      {customers &&
                        customers.length > 0 &&
                        customers
                          .filter((customer) => customer.name.toLowerCase().includes(customerSearchText.toLowerCase()))
                          .map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              <Checkbox checked={filterCustomer.includes(customer.id)} color="primary" />
                              <ListItemText primary={customer?.name} />
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Salesperson')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterSalesperson}
                      onChange={(e) => setFilterSalesperson(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterSalesperson) => {
                        if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterSalesperson.map((id) => salesPersonMap[id]).filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {salesPersons.map((salesperson) => (
                        <MenuItem key={salesperson.id} value={salesperson.id}>
                          <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                          <ListItemText primary={salesperson?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </>
          )}
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Bill no order')}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={sorting}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
                displayEmpty
              >
                <MenuItem value={'default'}>
                  <em>{tCommon('Default')}</em>
                </MenuItem>
                <MenuItem value={'DESC'}>{tCommon('Descending')}</MenuItem>
                <MenuItem value={'ASC'}>{tCommon('Ascending')}</MenuItem>
              </TextField>
            </div>
          </div>
          {ShopsHelper.hasPremiumAccess(shop) && (
            <div className={styles.filterDiv}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Customer Group')}</label>
              </div>
              <div>
                <FormControl>
                  <Select
                    multiple
                    displayEmpty
                    value={filterCustomerGroup}
                    onChange={(e) => setFilterCustomerGroup(e.target.value)}
                    input={<OutlinedInput />}
                    style={{ width: 135, height: 40 }}
                    renderValue={(selected) => {
                      if (selected.length === 0) return <span>{tCommon('All')}</span>;
                      const selectedNames = selected.map((id) => customerGroupMap[id]).filter(Boolean);
                      return selectedNames.join(', ');
                    }}
                    MenuProps={SelectFilterStyle}
                  >
                    {customerGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Checkbox checked={filterCustomerGroup.includes(group.id)} color="primary" />
                        <ListItemText primary={group.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          )}
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sale source')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = selected.map((id) => saleSourceMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {SALE_SOURCES.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      <Checkbox checked={filterSource.includes(source.id)} color="primary" />
                      <ListItemText primary={source.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sale Status')}</label>
            </div>
            <div>
              <TextField
                select
                name="status"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={filterStatus}
                onChange={(event) => {
                  setFilterStatus(event.target.value);
                }}
              >
                {Object.values(SALE_STATUS).map((s) => {
                  return <MenuItem value={s}>{tCommon(s)}</MenuItem>;
                })}
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummeryReport}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <>
            <div className={styles.summaryContainer}>
              <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalAmount}`}</div>
                <div className={styles.cardDetails}>
                  <span>
                    {tCommon('Total Bills')}: <strong className={styles.detailsValue}>{filteredItems.length}</strong>
                  </span>
                </div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
                <span className={styles.cardTitle}>{tCommon('Net Amount')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalNetAmount}`}</div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
                <span className={styles.cardTitle}>{tCommon('Total Tax')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</div>
                <div className={styles.cardDetails}>
                  <span>
                    {tCommon('Additional Tax')}:{' '}
                    <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${
                      summary.totalAdditionalTax
                    }`}</strong>
                  </span>
                </div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
                <span className={styles.cardTitle}>{tCommon('Discount')}</span>
                <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.discount}`}</div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={filteredItems ? filteredItems : items}
                rowKey="id"
                summary={{
                  date: tCommon('Total'),
                  net_amount: `${shop?.currency || ''} ${summary.totalNetAmount}`,
                  total: `${shop?.currency || ''} ${summary.totalAmount}`,
                  tax_amount: `${shop?.currency || ''} ${summary.totalTax}`,
                  cess_amount: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
                  discount_applied_on_items: `${shop?.currency || ''}  ${summary.discount}`,
                }}
                columnFilterId={STORAGE_KEYS.SALES_REPORT_COLUMN_FILTER}
                optionalColumns={optionalColumns}
                pagination={true}
              />
            </div>
          </>
        ) : (
          !loadingIndicator && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )
        ))
      )}

      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}

      {selectedBill && (
        <BillDetailsDialog
          handleClose={handleCloseSaleDetailsDialog}
          selectedSale={selectedBill}
          customers={customers}
        />
      )}
      {invoiceToVoid && (
        <RefundTypePopup
          closeDialog={() => {
            setInvoiceToVoid(null);
          }}
          handleRefundType={handleVoidInvoice}
          subject={'sale'}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(BillSummaryReport);
